// External packages
import * as React from 'react';
import { z } from 'zod';

// Components
import { Button } from 'components/Button';
import { Form, InputField } from 'components/Form';
import { Label } from 'components/Forms';
import * as Modal from 'components/Modal';
import { Option } from 'components/Select';
import { SelectMultiple } from 'components/SelectMultiple';
import { Tab, TabList, TabPanel, Tabs } from 'components/Tabs';
import { TiptapEditorField } from 'components/TiptapEditor';

// Modules
import { AudioSelectField } from 'modules/AudioSelectField';
import {
  AddValue,
  Language,
  TranslationsCleaner,
} from 'modules/exhibits/ExhibitsModal';
import { ImageSelectField } from 'modules/ImageSelectField';
import { PlaceMapSelectField } from 'modules/PlaceMapSelectField';
import { AddWorkingHours } from 'modules/places/AddWorkingHours';
import { VideoSelectField } from 'modules/VideoSelectField';

// Schemas
import { placeCreateSchema } from 'schemas/place';

// Data
import languagesData from 'data/languages.json';

type PlaceModalProps = {
  onSubmit: (data: z.infer<typeof placeCreateSchema>) => Promise<void> | void;
  defaultValues?: Partial<z.infer<typeof placeCreateSchema>>;
  modalTrigger: React.ReactNode;
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  isPending?: boolean;
};

export const PlaceModal: React.FC<PlaceModalProps> = ({
  onSubmit,
  defaultValues,
  modalTrigger,
  isOpen,
  onOpenChange,
  isPending,
}) => {
  const languages: Language[] = languagesData;

  const [selectedLanguages, setSelectedLanguages] = React.useState<Language[]>(
    () => {
      if (defaultValues && defaultValues.translations) {
        const langs = defaultValues.translations
          .map((t) => languages.find((l) => l.langCode === t.languageCode))
          .filter(Boolean) as Language[];

        return Array.from(new Set(langs.map((l) => l.langCode))).map(
          (code) => languages.find((l) => l.langCode === code)!
        );
      }

      return [languages[0]];
    }
  );

  const handleLanguageChange = (keys: unknown) => {
    let arr: string[] = Array.isArray(keys)
      ? keys.map(String)
      : keys instanceof Set
        ? Array.from(keys).map(String)
        : keys
          ? [String(keys)]
          : [];
    arr = Array.from(new Set(arr));

    if (arr.length === 0) {
      return;
    }

    const langs = arr
      .map((code) => languages.find((l) => l.langEnglishName === code))
      .filter(Boolean) as Language[];

    setSelectedLanguages(langs);
  };

  React.useEffect(() => {
    if (
      !defaultValues ||
      !defaultValues.translations?.some((t) =>
        selectedLanguages.some((l) => l.langCode === t.languageCode)
      )
    ) {
      return setSelectedLanguages([languages[0]]);
    }
  }, [defaultValues, languages]);

  return (
    <Modal.TriggerRoot isOpen={isOpen} onOpenChange={onOpenChange}>
      {modalTrigger}
      <Modal.Overlay isDismissable>
        <Modal.Root>
          <Modal.Dialog>
            <Modal.Close />
            <h3 className="mb-8 text-2xl text-gray-600">
              {defaultValues ? 'Uredi sobu' : 'Dodaj sobu'}
            </h3>
            <Form
              schema={placeCreateSchema}
              onSubmit={onSubmit}
              defaultValues={defaultValues}
            >
              <TranslationsCleaner selectedLanguages={selectedLanguages} />
              <InputField
                isRequired
                // isDisabled={!!defaultValues}
                name="name"
                inputProps={{ colorScheme: 'white' }}
                label="Naziv"
                className="flex-1 mb-4"
              />
              <div className="mb-4">
                <Label>Odaberi jezike</Label>
                <SelectMultiple
                  selectedKeys={selectedLanguages.map((l) => l.langEnglishName)}
                  onSelectionChange={handleLanguageChange}
                >
                  {languages &&
                    languages.map((lang) => (
                      <Option key={lang.langCode} id={lang.langEnglishName}>
                        {lang.langNativeName} ({lang.langEnglishName})
                      </Option>
                    ))}
                </SelectMultiple>
              </div>
              <Tabs className="mb-4">
                <TabList className="!justify-start">
                  {selectedLanguages.map((lang) => (
                    <Tab key={lang.langCode} id={lang.langEnglishName}>
                      {lang.langEnglishName}
                    </Tab>
                  ))}
                </TabList>
                {selectedLanguages.map((lang) => {
                  const index = selectedLanguages.findIndex(
                    (l) => l.langCode === lang.langCode
                  );

                  return (
                    <TabPanel
                      key={lang.langCode}
                      id={lang.langEnglishName}
                      className="px-4 py-6"
                    >
                      <AddValue index={index} langCode={lang.langCode} />
                      <InputField
                        isRequired
                        name={`translations.${index}.title`}
                        inputProps={{ colorScheme: 'white' }}
                        label={`Naslov (${lang.langEnglishName})`}
                        className="flex-1 mb-4"
                      />
                      <TiptapEditorField
                        isRequired
                        name={`translations.${index}.shortDescription`}
                        editorProps={{ colorScheme: 'white' }}
                        label={`Kratak opis (${lang.langEnglishName})`}
                        className="flex-1 mb-4"
                      />
                      <TiptapEditorField
                        isRequired
                        name={`translations.${index}.description`}
                        editorProps={{ colorScheme: 'white' }}
                        label={`Opis (${lang.langEnglishName})`}
                        className="flex-1 mb-4"
                      />
                      <VideoSelectField
                        clearable
                        label={`Video (${lang.langEnglishName})`}
                        placeholder="Dodaj video"
                        containerClassName="mb-4"
                        name={`translations.${index}.videoIds`}
                      />
                      <AudioSelectField
                        clearable
                        label={`Audio (${lang.langEnglishName})`}
                        placeholder="Dodaj audio"
                        name={`translations.${index}.audioIds`}
                      />
                    </TabPanel>
                  );
                })}
              </Tabs>
              <ImageSelectField
                clearable
                label="Slika"
                placeholder="Dodaj sliku"
                containerClassName="mb-4"
                name="imageIds"
              />
              <PlaceMapSelectField
                placeholder="Odaberi mapu"
                // isRequired
                name="mapId"
                label="Mapa"
                className="mb-4"
              />
              <AddWorkingHours label="Radno vrijeme" className="mb-4" />
              <Button
                type="submit"
                className="mt-12 w-full"
                size="lg"
                isPending={isPending}
              >
                {defaultValues ? 'Spremi promjene' : 'Dodaj sobu'}
              </Button>
            </Form>
          </Modal.Dialog>
        </Modal.Root>
      </Modal.Overlay>
    </Modal.TriggerRoot>
  );
};
